import { USER_PREFERRED_SORT_COOKIE_KEY } from "@app/constants/cookie-keys";
import type { AppCommonContext } from "@app/context/app-context";
import { isInterconnected } from "@app/helpers/route";
import { isReturn, isRoundtrip } from "@app/helpers/search";
import { LocalStorage } from "@app/lib/bowser-storage/local-storage";
import { sortDepartures } from "@app/modules/results/actions";
import { getFilters } from "@app/modules/results/selectors/state-accessors";
import {
  getDefaultSortOptions,
  isSortKey
} from "@app/modules/results/utils/sort-definitions";
import type { TranslatorService } from "@app/services/translator";
import type { Features } from "@app/types/experiments";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import type { Dispatch } from "redux";

import {
  BLABLACAR_SOURCE_ID,
  POPARIDE_SOURCE_ID,
  VehicleCategoryKey
} from "../../constants/filters";
import { getFilteredLegCards } from "../../selectors/leg-cards/get-filtered-leg-cards";
import { getLegCardIdsByVehicleCategory } from "../../selectors/leg-cards/get-leg-card-ids-by-vehicle-category";
import { isLegCardSoldOut } from "../../selectors/leg-cards/is-leg-card-sold-out";

export const shouldDisableExcludedSourcesFiltering = (
  state: ResultsReduxState
) => {
  const bus_leg_card_ids = getLegCardIdsByVehicleCategory(
    state,
    VehicleCategoryKey.Bus
  );

  const has_available_bus_legs = Object.keys(getFilteredLegCards(state)).some(
    leg_card_id =>
      bus_leg_card_ids.includes(leg_card_id) &&
      !isLegCardSoldOut(state, leg_card_id)
  );

  return !has_available_bus_legs;
};

export const shouldFetchAlternativesRoutes = (
  state: ResultsReduxState,
  context: AppCommonContext
) => {
  // At this point all departures in state have been updated.
  // If alternative_routes have already been populated on server side, this step is skipped.
  return (
    state.alternative_routes.length === 0 &&
    !isReturn(state.search) &&
    context.features.RESULTS_INVENTORY_SHOW_ALTERNATIVE_ROUTES
  );
};

export const shouldMakeAlternativeRoutesSearch = (state: ResultsReduxState) => {
  return (
    Object.keys(getFilteredLegCards(state)).length === 0 &&
    getFilters(state).vehicle_type.length === 0
  );
};

export const shouldFetchInterconnectedTrips = (state: ResultsReduxState) => {
  return (
    !isRoundtrip(state.search) &&
    isInterconnected(state.route) &&
    Object.keys(getFilteredLegCards(state)).length === 0
  );
};

export const getDefaultExcludedSources = (): Array<number> => {
  // hide Poparide and BBC inventory for everyone
  return [BLABLACAR_SOURCE_ID, POPARIDE_SOURCE_ID];
};

export const shouldResetFilters = (state: ResultsReduxState) => {
  if (Object.keys(getFilteredLegCards(state)).length > 0) {
    return false;
  }

  return Object.values(getFilters(state)).some(
    value => Array.isArray(value) && value.length > 0
  );
};

export const applySavedSortKey = (
  dispatch: Dispatch,
  translator: TranslatorService,
  features: Partial<Features>,
  country?: string
): void => {
  const saved_sort_key = LocalStorage.fromWindow(window).getItem(
    USER_PREFERRED_SORT_COOKIE_KEY
  );

  const sort_options = getDefaultSortOptions(translator, features, country);
  const is_sort_key_rendered = sort_options.find(o => o.key === saved_sort_key);

  if (
    typeof saved_sort_key === "string" &&
    isSortKey(saved_sort_key) &&
    is_sort_key_rendered
  ) {
    dispatch(sortDepartures(saved_sort_key));
  }
};

export const shouldFetchFallbackCities = (
  state: ResultsReduxState,
  poll_fallback_cities_url: string | undefined
) => {
  return (
    Object.keys(getFilteredLegCards(state)).length === 0 &&
    poll_fallback_cities_url !== undefined
  );
};

/**
 * operator ids can be injected via url, but the results may not contain the operator.
 * This function filters out the operator ids that are not present in the results
 *
 * @param state
 */
export const sanitizeOperatorIds = (state: ResultsReduxState) => {
  const filtered_operators = state.filters.operator_ids;
  const all_trips_operators = state.trips_operators;
  const operator_ids = filtered_operators.filter(id => all_trips_operators[id]);
  return operator_ids;
};

/**
 * profile ids can be injected via url, but the results may not contain the operator profile.
 * This function filters out the profile ids that are not present in the results
 *
 * @param state
 */
export const sanitizeOperatorProfileIds = (state: ResultsReduxState) => {
  const filtered_operator_profiles = state.filters.profile_ids;
  const all_trip_operator_profiles = new Set(
    Object.values(state.trips_operators)
      .map(o => o.profile_id)
      .filter(Boolean)
  );
  const operator_profile_ids = filtered_operator_profiles.filter(id =>
    all_trip_operator_profiles.has(id)
  );

  return operator_profile_ids;
};

/**
 * @param leg_card_ids
 * @param sponsored_leg_card_ids
 * @param max_sponsored_results (must be positive)
 * @returns [leg_card_ids[], sponsored_results[]]
 */
export const extractSponsoredLegCardIds = (
  leg_card_ids: string[],
  sponsored_leg_card_ids: string[],
  max_sponsored_results: number
): [string[], string[]] => {
  const sponsored_results: string[] = [];
  const new_leg_card_ids: string[] = [];
  leg_card_ids.forEach(leg_card_id => {
    if (
      sponsored_results.length < max_sponsored_results &&
      sponsored_leg_card_ids.includes(leg_card_id)
    ) {
      sponsored_results.push(leg_card_id);
    } else {
      new_leg_card_ids.push(leg_card_id);
    }
  });

  return [[...sponsored_results, ...new_leg_card_ids], sponsored_results];
};
